import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutationData } from "../../hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { TableWithPagination } from "../Tables";
import { formatDateTime } from "@utils/common";
import useEscapeKey from "@hooks/useEscapeKey";

const SbomClarityCompareModal = ({ show, onHide, projectSeq, onSelectVerification, "data-testid": dataTestId = "sbom-clarity-compare-modal" }) => {
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { t } = useTranslation();
  const { colors } = useTheme();

  // Local state management
  const [clarityVerifications, setClarityVerifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  // Add selectedVerification state for radio button selection
  const [selectedVerification, setSelectedVerification] = useState(null);

  useEscapeKey(onHide, show);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  // Reset state when modal opens
  useEffect(() => {
    if (show && projectSeq) {
      setCurrentPage(1);
      setItemsPerPage(5);
      // Reset selected verification when modal opens
      setSelectedVerification(null);
      fetchData();
    }
  }, [show, projectSeq]);

  // Fetch data when page or itemsPerPage changes (remove searchTerm dependency)
  useEffect(() => {
    if (show && projectSeq) {
      fetchData();
    }
  }, [currentPage, itemsPerPage, projectSeq]);

  // API call function
  const fetchData = () => {
    if (!projectSeq) return;

    setLoading(true);
    const requestBody = {
      pageSize: itemsPerPage,
      startRow: itemsPerPage * (currentPage - 1),
      projectSeq: projectSeq,
      // Remove searchTerm from API request
    };
    getBinaryVerificationsMutation.mutate(requestBody);
  };

  // API mutation
  const getBinaryVerificationsMutation = useMutationData(
    "binary_verification_by_project_seq",
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (response && response.object) {
          setClarityVerifications(response.object);
          setTotalData(response.objectCount);
          setTotalPages(Math.ceil(response.objectCount / itemsPerPage));
        }
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error fetching clarity verifications:", error);
        setLoading(false);
      },
    },
  );


  const handleClose = () => {
    onHide();
  };

  // Remove search-related functions
  // const handleSearchChange = (e) => { ... }
  // const handleSearchSubmit = (e) => { ... }

  const handleRowClick = (verification) => {
    setSelectedVerification(verification);
  };

  // Add radio selection logic for TableWithPagination
  const handleRadioTableChange = (selected) => {
    if (selected && selected.length > 0) {
      const verification = clarityVerifications.find((v) => v.binaryVerificationSeq === selected[0]);
      setSelectedVerification(verification);
    } else {
      setSelectedVerification(null);
    }
  };

  // Remove filteredData - use clarityVerifications directly
  // const filteredData = clarityVerifications.filter(...)

  // Define columns for the table
  const columns = [
    "verificationVersion",
    "realFileName",
    "verificationStatusCd",
    "requesterName",
    "requestedAt",
    "componentVersionCnt",
  ];

  // Define column configuration
  const columnConfig = {
    verificationVersion: {
      title: t("common.version"),
      customRender: (value, row) => (
        <span className={`text-sm font-medium ${colors.textPrimary}`}>{row.verificationVersion || "---"}</span>
      ),
      style: { width: "10%" },
    },
    realFileName: {
      title: t("project.sbom.verif-file-name"),
      customRender: (value, row) => (
        <span className={`text-sm font-medium ${colors.textPrimary}`}>{row.realFileName || "---"}</span>
      ),
      style: { width: "25%" },
    },
    verificationStatusCd: {
      title: t("project.sbom.verif-status"),
      customRender: (value, row) => {
        const statusStyles = {
          ON_ANALYSIS: colors.textStatusAnalysis,
          ANALYSIS_CHECK: colors.textStatusAnalysis,
          REQUEST: colors.textStatusRequest,
          ON_VERIFICATION: colors.textStatusVerification,
          ON_REVIEW: colors.textStatusReview,
          STANDBY_FOLLOWUP: colors.textStatusFollowup,
          FOLLOWUP_CHECK: colors.textStatusFollowup,
          DONE: colors.textStatusSuccess,
          REVERIFICATION: colors.textStatusReverification,
          ERROR: colors.textStatusAnalysis,
        };

        const statusColorDot = {
          ON_ANALYSIS: colors.bgStatusAnalysis,
          ANALYSIS_CHECK: colors.bgStatusAnalysis,
          REQUEST: colors.bgStatusRequest,
          ON_VERIFICATION: colors.bgStatusVerification,
          ON_REVIEW: colors.bgStatusReview,
          STANDBY_FOLLOWUP: colors.bgStatusFollowup,
          FOLLOWUP_CHECK: colors.bgStatusFollowup,
          DONE: colors.bgStatusSuccess,
          REVERIFICATION: colors.bgStatusReverification,
          ERROR: colors.bgStatusAnalysis,
        };

        const statusText = {
          ON_ANALYSIS: t("status.analyzing"),
          ANALYSIS_CHECK: t("status.analysisCheck"),
          REQUEST: t("status.request"),
          ON_VERIFICATION: t("status.onVerification"),
          ON_REVIEW: t("status.onReview"),
          STANDBY_FOLLOWUP: t("status.standbyFollowup"),
          FOLLOWUP_CHECK: t("status.followupCheck"),
          DONE: t("status.done"),
          REVERIFICATION: t("status.reverification"),
          ERROR: t("status.error"),
        };

        // For ON_VERIFICATION status, use the specific status name if available
        let displayText = statusText[row.verificationStatusCd];
        if (row.verificationStatusCd === "ON_VERIFICATION" && row.verificationStatusCdName) {
          // Map the status name to translation keys
          const statusNameMap = {
            "Usage Type Review": t("status.usageTypeReview"),
            "Usage Type Check": t("status.usageTypeCheck"),
            "Usage Type Input": t("status.usageTypeInput"),
          };
          displayText = statusNameMap[row.verificationStatusCdName] || row.verificationStatusCdName;
        }

        return (
          <span
            className={`inline-flex items-center py-0.5 text-sm text-[13px] leading-none font-semibold ${statusStyles[row.verificationStatusCd] || `${colors.bgStatusTagNeutral} ${colors.textPrimary}`}`}
          >
            <div className={`mr-2 h-2 w-2 rounded-full ${statusColorDot[row.verificationStatusCd]}`}></div>
            {displayText || row.verificationStatusCd || "---"}
          </span>
        );
      },
      style: { width: "20%" },
    },
    requesterName: {
      title: t("common.requester"),
      customRender: (value, row) => (
        <span className={`text-sm font-medium ${colors.textPrimary}`}>{row.requesterName || "Superuser"}</span>
      ),
      style: { width: "15%" },
    },
    requestedAt: {
      title: t("common.requested-at"),
      customRender: (value, row) => (
        <span className={`text-sm font-medium ${colors.textPrimary}`}>
          {row.requestedAt
            ? formatDateTime(row.requestedAt)
            : "---"}
        </span>
      ),
      style: { width: "20%" },
    },
    componentVersionCnt: {
      title: t("project.sbom.detected-component"),
      customRender: (value, row) => (
        <span className={`text-sm font-medium ${colors.textPrimary}`}>{row.componentVersionCnt || "0"}</span>
      ),
      style: { width: "10%" },
    },
  };

  if (!show) return null;

  return (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start xl:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`} data-testid={`${dataTestId}-overlay`}>
      <div className={`${colors.bgModal} w-[1200px] min-w-[1200px] max-h-[90vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl xl:overflow-y-hidden flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className="px-6 py-4 flex-shrink-0" data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
              {t("project.sbom.sbom-verification-comparison")}
            </h2>
          </div>
          <p className={`${colors.textSecondary} text-sm mt-1`} data-testid={fieldValueTestId("instruction")}>
            {t("project.sbom.select-clarity-verification-instruction")}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 pb-4 flex-1 min-h-0 flex flex-col" data-testid={`${dataTestId}-content`}>
          <TableWithPagination
            columns={columns}
            data={clarityVerifications}
            colSize={12}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            loading={loading}
            totalPages={totalPages}
            totalData={totalData}
            columnConfig={columnConfig}
            onRowClick={handleRowClick}
            maxRowsPerPage={itemsPerPage}
            onItemsPerPageChange={setItemsPerPage}
            showPagination={true}
            tableStyle="default"
            rowKey="binaryVerificationSeq"
            hasRadioButton={true}
            selectedRows={selectedVerification ? [selectedVerification.binaryVerificationSeq] : []}
            onCheckboxChange={handleRadioTableChange}
            dataTestId={`${dataTestId}-table`}
          />
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <div className="flex justify-end gap-4">
            <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleClose}>{t("common.cancel-button")}</OutlineButton>
            <PrimaryButton
              data-testid={`${dataTestId}-confirm-button`}
              onClick={() => onSelectVerification(selectedVerification)}
              disabled={!selectedVerification}
            >
              {t("common.compare-button", "Compare")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SbomClarityCompareModal;
